import axios from "axios";

import { API_URL } from "@/config/constants";

import {
    REPORT_REFUSAL_CODES,
    REPORT_RETRY_REFUSAL_CODES,
    type IReportVersionSummary,
    type ReportRetryErrorCode,
} from "./reports.types";

const conversationReportsUrl = (conversationId: number) =>
    `${API_URL}/chat/conversations/${conversationId}/reports`;

const RETRY_ERROR_CODES = new Set<string>([
    ...REPORT_REFUSAL_CODES,
    ...REPORT_RETRY_REFUSAL_CODES,
]);

/** A Retry the server refused with a known code; the version keeps its status. */
export class ReportRetryError extends Error {
    public readonly code: ReportRetryErrorCode;
    public readonly status: number;

    constructor(code: ReportRetryErrorCode, status: number) {
        super(code);
        this.name = "ReportRetryError";
        this.code = code;
        this.status = status;
    }
}

function toReportRetryError(error: unknown): ReportRetryError | null {
    if (!axios.isAxiosError(error)) return null;
    const status = error.response?.status;
    if (status !== 403 && status !== 409) return null;
    const data: unknown = error.response?.data;
    if (!data || typeof data !== "object") return null;
    const detail = (data as { detail?: unknown }).detail;
    if (typeof detail !== "string" || !RETRY_ERROR_CODES.has(detail)) {
        return null;
    }
    return new ReportRetryError(detail as ReportRetryErrorCode, status);
}

export const getConversationReports = async (
    conversationId: number,
    signal?: AbortSignal
) => {
    const result = await axios.get<IReportVersionSummary[]>(
        conversationReportsUrl(conversationId),
        { signal }
    );
    return result.data;
};

export const retryReport = async (
    conversationId: number,
    versionId: number
) => {
    try {
        const result = await axios.post<IReportVersionSummary>(
            `${conversationReportsUrl(conversationId)}/${versionId}/retry`
        );
        return result.data;
    } catch (error) {
        throw toReportRetryError(error) ?? error;
    }
};
